import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  IconButton,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { Add, Edit, Delete, Save, Close } from "@mui/icons-material";
import axios from "axios";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../../Components/Snackbar/SnackbarContext";

const FeeStructure = ({ feeItems = [], refresh }) => {
  const { showSnackbar } = useSnackbar();

  const [activeType, setActiveType] = useState("standard"); // "standard" or "custom"

  const [branchList, setBranchList] = useState([]);
  const [classList, setClassList] = useState([]);

  const [branchGuid, setBranchGuid] = useState("");
  const [classGuid, setClassGuid] = useState("");

  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("");

  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});

  const token = localStorage.getItem("token");

  const frequencies = ["monthly", "yearly", "one_time"];

  useEffect(() => {
    loadBranches();
  }, []);

  // -----------------------------
  // Load branches
  // -----------------------------
  const loadBranches = async () => {
    try {
      const res = await axios.get(
        `${apiEndpoints.dropdown}?type=branch&mode=initial`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBranchList(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      showSnackbar("Failed to load branches", "error");
    }
  };

  // -----------------------------
  // Load classes for branch
  // -----------------------------
  const loadClasses = async (branch) => {
    if (!branch) {
      setClassList([]);
      return;
    }
    try {
      const res = await axios.get(
        `${apiEndpoints.dropdown}?type=class&branch_guid=${branch}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClassList(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      showSnackbar("Failed to load classes", "error");
    }
  };

  // called when branch changed in form
  const handleBranchChange = (value) => {
    setBranchGuid(value);
    setClassGuid("");
    loadClasses(value);
  };

  const resetForm = () => {
    setBranchGuid("");
    setClassGuid("");
    setLabel("");
    setAmount("");
    setFrequency("");
    setErrors({});
    setEditId(null);
  };

  // -----------------------------
  // Submit create / update
  // -----------------------------
  const handleSubmit = async () => {
    const temp = {};
    if (!label) temp.label = "Required";
    if (!amount) temp.amount = "Required";
    if (!branchGuid) temp.branchGuid = "Branch required";
    if (activeType === "standard") {
      if (!classGuid) temp.classGuid = "Class required";
      if (!frequency) temp.frequency = "Frequency required";
    }

    if (Object.keys(temp).length > 0) {
      setErrors(temp);
      return;
    }

    const body =
      activeType === "standard"
        ? {
            id: editId,
            label,
            amount,
            branch_guid: branchGuid,
            class_guid: classGuid,
            frequency,
          }
        : {
            id: editId,
            label,
            amount,
            branch_guid: branchGuid,
          };

    try {
      if (editId) {
        await axios.put(
          `${apiEndpoints.fee_management}?type=${activeType}&action=update`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar("Fee updated", "success");
      } else {
        await axios.post(
          `${apiEndpoints.fee_management}?type=${activeType}&action=create`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar("Fee created", "success");
      }

      // refresh parent list
      if (typeof refresh === "function") await refresh();
      resetForm();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Operation failed";
      showSnackbar(msg, "error");
    }
  };

  // -----------------------------
  // Edit item (prefill form)
  // -----------------------------
  const editItem = (item) => {
    setEditId(item.id);
    setLabel(item.label ?? "");
    setAmount(item.amount ?? "");
    setBranchGuid(item.branch_guid ?? "");
    setClassGuid(item.class_guid ?? "");
    setFrequency(item.frequency ?? "");

    // load classes for branch so class dropdown has values
    if (item.branch_guid) loadClasses(item.branch_guid);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // -----------------------------
  // Delete (soft delete)
  // -----------------------------
  const removeItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this fee?")) return;

    try {
      await axios.delete(
        `${apiEndpoints.fee_management}?type=${activeType}&action=delete&id=${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("Fee deleted", "success");
      if (typeof refresh === "function") await refresh();
    } catch (err) {
      showSnackbar("Delete failed", "error");
    }
  };

  // Filter feeItems passed from parent by type
  const filteredItems = Array.isArray(feeItems)
    ? feeItems.filter((i) => i.type === activeType)
    : [];

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        background: "rgba(255,255,255,0.5)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.5)",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
      }}
    >
      <Tabs
        value={activeType}
        onChange={(e, v) => {
          setActiveType(v);
          resetForm();
        }}
        sx={{ mb: 3 }}
      >
        <Tab label="Standard Fee" value="standard" />
        <Tab label="Custom Fee" value="custom" />
      </Tabs>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {editId
          ? "Edit Fee"
          : activeType === "standard"
          ? "Add Standard Fee"
          : "Add Custom Fee"}
      </Typography>

      <Grid container spacing={2}>
        {/* Label */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Fee Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            error={!!errors.label}
            helperText={errors.label}
            InputProps={{ sx: { borderRadius: 3 } }}
          />
        </Grid>

        {/* Branch */}
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            label="Branch"
            value={branchGuid}
            onChange={(e) => handleBranchChange(e.target.value)}
            error={!!errors.branchGuid}
            helperText={errors.branchGuid}
            InputProps={{ sx: { borderRadius: 3 } }}
          >
            <MenuItem value="">Select branch</MenuItem>
            {branchList.map((b) => (
              <MenuItem key={b.guid} value={b.guid}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Class — only for standard */}
        {activeType === "standard" && (
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Class"
              value={classGuid}
              onChange={(e) => setClassGuid(e.target.value)}
              error={!!errors.classGuid}
              helperText={errors.classGuid}
              InputProps={{ sx: { borderRadius: 3 } }}
            >
              <MenuItem value="">Select class</MenuItem>
              {classList.map((c) => (
                <MenuItem key={c.guid} value={c.guid}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {/* Amount */}
        <Grid item xs={12} md={4}>
          <TextField
            type="number"
            fullWidth
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={!!errors.amount}
            helperText={errors.amount}
            InputProps={{ sx: { borderRadius: 3 } }}
          />
        </Grid>

        {/* Frequency */}
        {activeType === "standard" && (
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              error={!!errors.frequency}
              helperText={errors.frequency}
              InputProps={{ sx: { borderRadius: 3 } }}
            >
              <MenuItem value="">Select frequency</MenuItem>
              {frequencies.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>

      {/* Submit */}
      <Grid container spacing={2} mt={3}>
        <Grid item xs={12} md={3}>
          <Button
            fullWidth
            variant="contained"
            startIcon={editId ? <Save /> : <Add />}
            onClick={handleSubmit}
            sx={{ borderRadius: 999, px: 3 }}
          >
            {editId ? "Update" : "Add"}
          </Button>
        </Grid>

        {editId && (
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              color="error"
              startIcon={<Close />}
              onClick={resetForm}
              sx={{ borderRadius: 3 }}
            >
              Cancel Edit
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Fee list */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {activeType === "standard"
            ? "Standard Fee Structure"
            : "Custom Fee Structure"}
        </Typography>

        {filteredItems.length === 0 ? (
          <Typography>No fee records found.</Typography>
        ) : (
          filteredItems.map((item) => (
            <Paper
              key={item.id}
              sx={{
                p: 2,
                mb: 1.5,
                borderRadius: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "rgba(255,255,255,0.6)",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                  {item.label}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  ₹{item.amount}
                </Typography>

                <Typography variant="body2">
                  Branch: {item.branch_name ?? item.branch_guid}
                </Typography>

                {activeType === "standard" && (
                  <>
                    <Typography variant="body2">
                      Class: {item.class_name ?? item.class_guid}
                    </Typography>
                    <Typography variant="body2">
                      Frequency: {item.frequency}
                    </Typography>
                  </>
                )}
              </Box>

              <Box>
                <IconButton color="primary" onClick={() => editItem(item)}>
                  <Edit />
                </IconButton>

                <IconButton color="error" onClick={() => removeItem(item.id)}>
                  <Delete />
                </IconButton>
              </Box>
            </Paper>
          ))
        )}
      </Box>
    </Paper>
  );
};

export default FeeStructure;
